import { decode } from 'blurhash';
import Head from 'next/head';
import React, { type CSSProperties, useEffect } from 'react';

import type { SanityImageType } from '../../data/sanity/types';
import { FALLBACK_IMAGE } from '../../utils/constants';
import {
  BlurHashCanvas,
  WatermarkImage,
  WatermarkText,
} from '../WatermarkText/WatermarkText';
import {
  useResponsiveContentImage,
  useResponsiveSanityImage,
} from './SemboImage.hooks';
import { ImageWrapper, StyledImage } from './SemboImage.styles';
import type { ImageOptions, ViewportsSizesProps } from './SemboImage.types';

type SemboImageProps = React.ComponentPropsWithoutRef<'img'> &
  Partial<ViewportsSizesProps> & {
    sanityImage?: SanityImageType | null;
    contentImage?: string;
    imageService?: string;
    fallbackImage?: SanityImageType | null | string;
    square?: boolean;
    height?: number;
    minHeight?: number;
    wrapperStyle?: CSSProperties;
    priority?: boolean | `(${string})`;
    loading?: 'eager' | 'lazy';
    fetchpriority?: 'high' | 'low' | 'auto';
    wrapperComponent?: React.ElementType;
  } & Partial<ImageOptions> & { src?: never };

export const SemboImage = React.forwardRef<HTMLImageElement, SemboImageProps>(
  (props, ref) => {
    const {
      alt,
      xxxs: XXXS,
      xxs: XXS,
      xs: XS,
      sm: SM,
      md: MD,
      lg: LG,
      explicitXl: XL,
      explicitXxl: XXL,
      explicitXxxl: XXXL,
      sanityImage,
      contentImage,
      imageService,
      fallbackImage,
      wrapperStyle,
      square,
      height,
      minHeight,
      children,
      style,
      quality,
      priority,
      loading,
      wrapperComponent,
      aspectRatio,
      ...imgProps
    } = props;

    const sanityImageAlreadyLoaded = React.useRef(false);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const canvasLoaded = React.useRef(false);
    const WrapperComponent = wrapperComponent ?? ImageWrapper;

    const { alt: sanityImageAlt, ...sanityImageProps } =
      useResponsiveSanityImage(
        { XXXS, XXS, XS, SM, MD, LG, XL, XXL, XXXL },
        sanityImage,
        { height, minHeight, quality, square, aspectRatio }
      );

    const contentImageProps = useResponsiveContentImage(
      { XXXS, XXS, XS, SM, MD, LG, XL, XXL, XXXL },
      contentImage,
      imageService,
      { height, square, aspectRatio }
    );

    const [imgError, setImgError] = React.useState(false);

    useEffect(() => {
      if (!sanityImage?.blurHash) {
        return;
      }

      if (sanityImageAlreadyLoaded.current) {
        if (canvasRef?.current) {
          canvasRef.current.width = 0;
          canvasRef.current.height = 0;
          canvasRef.current.style.display = 'none';
        }
      } else if (canvasRef?.current && !canvasLoaded.current) {
        canvasLoaded.current = true;

        const width = 32;
        const height = 32;
        const pixels = decode(sanityImage?.blurHash, 32, 32);
        const ctx = canvasRef?.current?.getContext('2d');

        if (ctx) {
          const imageData = ctx.createImageData(width, height);
          imageData.data.set(pixels);
          ctx.putImageData(imageData, 0, 0);
        }
      }
    }, [sanityImage?.blurHash]);

    if (imgError || (!sanityImageProps.src && !contentImageProps.src)) {
      // "yo dawg, I heard you like SemboImage, so we put SemboImage in your SemboImage"
      const evaluatedFallbackImage = fallbackImage ?? FALLBACK_IMAGE;

      return (
        <SemboImage
          ref={ref}
          {...props}
          sanityImage={
            typeof evaluatedFallbackImage !== 'string'
              ? evaluatedFallbackImage
              : undefined
          }
          contentImage={
            typeof evaluatedFallbackImage === 'string'
              ? evaluatedFallbackImage
              : undefined
          }
          aspectRatio={aspectRatio}
          fallbackImage={undefined}
          quality={100}
        />
      );
    }

    const handleError = () => {
      setImgError(true);
    };

    return (
      <>
        {priority && (sanityImageProps.src || contentImageProps.src) && (
          <Head>
            <link
              rel="preload"
              as="image"
              media={typeof priority === 'string' ? priority : undefined}
              href={sanityImageProps.src ?? contentImageProps.src}
              imageSrcSet={sanityImageProps.srcSet ?? contentImageProps.srcSet}
              // @ts-expect-error not sure why react doesn't like this
              imageSizes={sanityImageProps.sizes ?? contentImageProps.sizes}
            />
          </Head>
        )}

        <WrapperComponent style={wrapperStyle}>
          <StyledImage
            ref={(ref) => {
              if (sanityImage && ref?.complete) {
                sanityImageAlreadyLoaded.current = true;
              }

              return ref;
            }}
            {...sanityImageProps}
            {...contentImageProps}
            alt={alt ?? sanityImageAlt ?? ''}
            {...imgProps}
            loading={loading ?? 'lazy'}
            onLoad={() => {
              if (sanityImage && canvasRef.current) {
                canvasRef.current.style.opacity = '0';
              }
            }}
            style={{
              ...sanityImageProps?.style,
              ...style,
              aspectRatio: aspectRatio
                ? aspectRatio.replace(':', ' / ')
                : undefined,
            }}
            onError={handleError}
          />
          {!priority && sanityImage?.blurHash ? (
            <BlurHashCanvas
              onTransitionEnd={(e) => {
                if (
                  e.currentTarget === e.target &&
                  e.propertyName === 'opacity'
                ) {
                  const canvas = e.currentTarget;
                  canvas.width = 0;
                  canvas.height = 0;
                  canvas.style.display = 'none';
                }
              }}
              height={32}
              width={32}
              ref={canvasRef}
            />
          ) : null}
          {sanityImage?.watermarkText && (
            <WatermarkText>{sanityImage.watermarkText}</WatermarkText>
          )}
          {sanityImage?.watermarkImage && (
            <WatermarkImage>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={sanityImage.watermarkImage}
                alt={alt ?? ''}
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            </WatermarkImage>
          )}
          {children}
        </WrapperComponent>
      </>
    );
  }
);

SemboImage.displayName = 'SemboImage';
